import { useCallback, useState } from 'react'

/**
 * Which cards a person's project overview shows, and in what order.
 *
 * The default is the overview as it was -- what is in your court and the
 * activity on the left, the leaderboard on the right -- with the newer cards
 * after them. A person turns cards off and moves them up and down from
 * "Customize"; nothing about one person's choice reaches anybody else.
 *
 * **Kept in this browser, per person.** The choice is stored under the
 * person's id in this browser's own storage, so it holds across every project
 * they open here and a second person signing in on the same laptop gets their
 * own. It does not follow them to another device. A browser that refuses
 * storage shows the default and keeps a change for as long as the page is open.
 */
export const CARDS = ['court', 'activity', 'chart', 'forecast', 'board', 'milestones', 'photos'] as const
export type CardKey = (typeof CARDS)[number]

/** Which column a card sits in on a wide screen. One column below 1100 px. */
export const COLUMN: Record<CardKey, 'main' | 'side'> = {
  court: 'main',
  activity: 'main',
  chart: 'main',
  forecast: 'side',
  board: 'side',
  milestones: 'side',
  photos: 'side',
}

export type Layout = { order: CardKey[]; hidden: CardKey[] }

export const DEFAULT_LAYOUT: Layout = { order: [...CARDS], hidden: [] }

function storageKey(personId: string): string {
  return `sitefocus.dashboard.${personId}`
}

function isCard(value: unknown): value is CardKey {
  return typeof value === 'string' && (CARDS as readonly string[]).includes(value)
}

/**
 * A stored layout, made whole. Cards it does not name -- one added by a later
 * build -- go on the end and are shown, and names this build does not know are
 * dropped, so an old choice never hides a new card or breaks the page.
 */
export function normalise(raw: unknown): Layout {
  if (typeof raw !== 'object' || raw === null) return DEFAULT_LAYOUT
  const { order, hidden } = raw as { order?: unknown; hidden?: unknown }
  const known = Array.isArray(order) ? order.filter(isCard) : []
  const unique = known.filter((card, index) => known.indexOf(card) === index)
  const missing = CARDS.filter((card) => !unique.includes(card))
  return {
    order: [...unique, ...missing],
    hidden: Array.isArray(hidden) ? hidden.filter(isCard) : [],
  }
}

export function readLayout(personId: string | null | undefined): Layout {
  if (!personId) return DEFAULT_LAYOUT
  try {
    const stored = window.localStorage.getItem(storageKey(personId))
    return stored ? normalise(JSON.parse(stored)) : DEFAULT_LAYOUT
  } catch {
    return DEFAULT_LAYOUT
  }
}

function writeLayout(personId: string | null | undefined, layout: Layout): void {
  if (!personId) return
  try {
    window.localStorage.setItem(storageKey(personId), JSON.stringify(layout))
  } catch {
    // Storage refused: the choice lasts as long as the page, which is said above.
  }
}

/** The layout for this person, and a way to change it that also keeps it. */
export function useDashboardLayout(
  personId: string | null | undefined,
): [Layout, (next: Layout) => void] {
  const [held, setHeld] = useState<{
    personId: typeof personId
    layout: Layout
  }>(() => ({
    personId,
    layout: readLayout(personId),
  }))
  // A different person on the same screen reads their own, not the last one's.
  const layout = held.personId === personId ? held.layout : readLayout(personId)
  if (held.personId !== personId) setHeld({ personId, layout })

  const change = useCallback(
    (next: Layout) => {
      const whole = normalise(next)
      writeLayout(personId, whole)
      setHeld({ personId, layout: whole })
    },
    [personId],
  )
  return [layout, change]
}

/**
 * Move one card up (-1) or down (+1) past the next card this reader can see
 * in the same column, which is the only move a reader can see: swapping with a
 * card in the other column, or with a card this person is not offered, would
 * change nothing on the screen and leave the button looking broken.
 *
 * `offered` is the cards this person may have on this job. A card not offered
 * keeps its place in the saved order, so it is where they left it if it is
 * offered again later.
 */
export function move(
  layout: Layout,
  card: CardKey,
  by: -1 | 1,
  offered: readonly CardKey[] = CARDS,
): Layout {
  const order = [...layout.order]
  const from = order.indexOf(card)
  if (from < 0) return layout
  let to = from + by
  while (
    to >= 0 &&
    to < order.length &&
    (COLUMN[order[to] as CardKey] !== COLUMN[card] || !offered.includes(order[to] as CardKey))
  )
    to += by
  if (to < 0 || to >= order.length) return layout
  ;[order[from], order[to]] = [order[to] as CardKey, order[from] as CardKey]
  return { ...layout, order }
}

/** Show or hide one card. */
export function toggle(layout: Layout, card: CardKey): Layout {
  const hidden = layout.hidden.includes(card)
    ? layout.hidden.filter((each) => each !== card)
    : [...layout.hidden, card]
  return { ...layout, hidden }
}
